<template>
  <MultiLevelNavigationModal
    v-model="localValue"
    :modal-title="$t('selectAttributeGroup')"
    :fetch-branches="getAttributeGroups"
    :fetch-leaves="getAttributes"
    :selectable-type="['branch']"
    :selected-entry-id="selectedEntryId"
    :selected-path="selectedPath"
    :base-path-title="$t('baseAttributeGroup')"
    :root-selection="vm.rootSelection"
    @confirm="onConfirm"
    @hide="emits('hide')"
    @cancel="emits('cancel')"
    @close="emits('close')" />
</template>

<script setup lang="ts">
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { AttributeGroup } from "@medapro-types/common/common";
import { Branch, Leaf, MultiLevelNavigationConfirmModel, MultiLevelNavigationSelectionModel } from "@medapro-types/common/components/MultiLevelNavigation";
import articleApi from "@services/article.js";
import { BaseIds } from "@util/enums/base-ids";
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import MultiLevelNavigationModal from "./MultiLevelNavigationModal.vue";

const BRANCH_ICON = "AttributeGroup1";

//#region General
const emits = defineEmits(["confirm", "cancel", "close", "input", "hide"]);

const props = defineProps<{
  value: boolean;
  selectedEntryId: string | null | undefined;
  selectedPath: Branch[] | null | undefined;
}>();

const { t: $t } = useI18n();
type ViewModel = { rootSelection: MultiLevelNavigationSelectionModel | null };
const vm = reactive<ViewModel>({ rootSelection: null });
//#endregion

//#region Store

const { primaryLanguageCode } = useContentLanguages();

//#endregion

const onConfirm = (event: MultiLevelNavigationConfirmModel) => {
  if (confirm($t("confirm.change.attributeGroup"))) emits("confirm", event);
};

//#region CONSTANTS
const BASE_ATTRIBUTE_GROUP_ID = BaseIds.BaseAttributeGroup;
//#endregion

//#region localValue
const localValue = computed({
  get: () => props.value,
  set: (nv) => emits("input", nv)
});

//#endregion

//#region FetchMethods
const getAttributeGroups = async (groupId: string | null | undefined): Promise<Branch[]> => {
  if (!groupId) {
    vm.rootSelection = {
      icon: "Home",
      id: BASE_ATTRIBUTE_GROUP_ID,
      name: `<${$t("baseAttributeGroup")}>`,
      type: "all"
    };
  } else {
    vm.rootSelection = null;
  }
  const fetchResult: AttributeGroup[] = await articleApi.getAttributeGroups({
    language: primaryLanguageCode.value,
    id: groupId ?? BASE_ATTRIBUTE_GROUP_ID
  });
  return fetchResult.map((x) => ({ id: x.attributeGroupId, depth: x.depth, name: x.name, parentId: x.parentId, icon: BRANCH_ICON }));
};

const getAttributes = async (): Promise<Leaf[]> => {
  return [];
};
//#endregion
</script>
